import { Calendar, MapPin, Users, Mail, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { InterestDialog } from "@/components/dialogs/InterestDialog";
import { ASSETS, ORG } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const AGE_GROUPS = ["10U", "11U", "12U", "13U", "14U", "15U", "16U", "17U"];
const DETAILS = [
  { icon: Calendar, label: "Tryout Dates", value: "To be announced", note: "Spring & Fall seasons" },
  { icon: MapPin, label: "Location", value: "Norwalk, CT — venue TBA", note: "Details sent after signup" },
  { icon: Users, label: "Divisions", value: "Boys & Girls", note: "Ages 10U–17U" },
];

export default function AAUTryouts() {
  useSEO({
    title: "AAU Basketball Tryouts in Norwalk, CT | CT BLAZERS Boys & Girls",
    description:
      "CT BLAZERS competitive AAU basketball tryouts for boys and girls ages 10U–17U in Norwalk, CT. Spring & Fall seasons — tryout required. Register your interest today.",
    keywords:
      "AAU Basketball Norwalk CT, AAU Basketball Connecticut, Boys AAU Basketball Connecticut, Girls AAU Basketball Connecticut, AAU tryouts CT",
    path: "/aau-tryouts",
  });

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="Spring & Fall · Tryout Required"
        title="AAU"
        highlight="Tryouts"
        image={ASSETS.boysTeam}
        subtitle="CT BLAZERS fields competitive AAU teams for boys and girls during the Spring and Fall seasons. A tryout is required to join a team — here's how to get your player involved."
      >
        <InterestDialog category="tryout" program="AAU Tryouts (Spring & Fall)" testId="aau">
          <Button
            data-testid="aau-register-btn"
            className="bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 px-8 shadow-[0_10px_30px_rgba(246,140,31,0.4)] transition-all hover:-translate-y-1"
          >
            Register For Tryouts
          </Button>
        </InterestDialog>
      </PageHeader>

      <section className="py-16 sm:py-24 bg-ink-800">
        <div className="container-x">
          <div className="grid gap-6 sm:grid-cols-3">
            {DETAILS.map((d, i) => (
              <Reveal key={d.label} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-ink-700 p-6 h-full">
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-blaze-blue/15 text-blaze-blueLight">
                    <d.icon size={22} />
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-wider text-white/50">{d.label}</p>
                  <p className="mt-1 font-heading text-2xl tracking-wide text-white">{d.value}</p>
                  <p className="mt-1 text-sm text-white/50">{d.note}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-10 rounded-2xl border border-white/10 bg-ink-700/60 p-8">
              <h2 className="font-display font-black uppercase tracking-tight text-3xl text-white">Age Groups</h2>
              <p className="mt-2 text-white/60">Competitive teams for boys and girls across the following divisions:</p>
              <div className="mt-6 flex flex-wrap gap-3">
                {AGE_GROUPS.map((a) => (
                  <span
                    key={a}
                    data-testid={`age-${a}`}
                    className="rounded-full border border-blaze-blue/40 bg-blaze-blue/10 px-5 py-2 font-heading text-xl tracking-wide text-white"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-ink-700/60 p-8">
                <h3 className="font-heading text-2xl tracking-wide text-white">How To Get Involved</h3>
                <ul className="mt-4 space-y-3">
                  {["Register your interest below", "We'll confirm tryout date & location", "Attend the tryout", "Get placed on a competitive team"].map((s) => (
                    <li key={s} className="flex items-start gap-3 text-white/70">
                      <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-blaze-orange" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-blaze-blue/20 to-ink-700 p-8 flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-2xl tracking-wide text-white">Questions?</h3>
                  <p className="mt-2 text-white/70">Reach out and we'll help you find the right fit for your player.</p>
                  <a href={`mailto:${ORG.email}`} className="mt-4 inline-flex items-center gap-2 text-blaze-orange font-semibold break-all">
                    <Mail size={16} /> {ORG.email}
                  </a>
                </div>
                <InterestDialog category="tryout" program="AAU Tryouts (Spring & Fall)" testId="aau-bottom">
                  <Button
                    data-testid="aau-register-btn-2"
                    className="mt-6 bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12"
                  >
                    Register For Tryouts
                  </Button>
                </InterestDialog>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
